import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Menu } from 'antd';
import styled from 'styled-components';

/**
 * One shared inline status control for an `Expense`, used everywhere an
 * expense row is listed (the register, the payment calendar, the teacher
 * payouts). A coloured pill that, when interactive, opens a mini-menu of the
 * four statuses — so a finance manager flips paid / unpaid / confirmed /
 * cancelled in one click without opening the card.
 *
 * `value` is tolerant of casing: the register surfaces the graphene auto-enum
 * (`'PAID'`), the calendar surfaces the lowercase row status (`'paid'`). We
 * normalise to a lowercase code for display and ALWAYS emit a lowercase code
 * from `onChange` — the form-less mutations want the raw lowercase value.
 */

export const EXPENSE_STATUS_CODES = [
  'planned',
  'confirmed',
  'paid',
  'cancelled',
] as const;

export type ExpenseStatusCode = typeof EXPENSE_STATUS_CODES[number];

type Tone = 'ok' | 'danger' | 'muted';

const STATUS_TONE: Record<ExpenseStatusCode, Tone> = {
  paid: 'ok',
  confirmed: 'ok',
  cancelled: 'danger',
  planned: 'muted',
};

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  ok: { bg: 'var(--k-bg-16a34a)', fg: 'var(--k-fg-ffffff)' },
  danger: { bg: 'var(--k-bg-dc2626)', fg: 'var(--k-fg-ffffff)' },
  muted: { bg: 'var(--k-bg-e2e8f0)', fg: 'var(--k-fg-475569)' },
};

const normalize = (value?: string | null): ExpenseStatusCode => {
  const code = (value || '').toLowerCase();
  return (EXPENSE_STATUS_CODES as readonly string[]).includes(code)
    ? (code as ExpenseStatusCode)
    : 'planned';
};

interface IExpenseStatusControlProps {
  value?: string | null;
  /** Receives the chosen LOWERCASE status code. */
  onChange?: (status: ExpenseStatusCode) => void;
  /** Render a static pill with no menu (synced income rows). */
  readOnly?: boolean;
  loading?: boolean;
}

export const ExpenseStatusControl = memo(
  ({ value, onChange, readOnly, loading }: IExpenseStatusControlProps) => {
    const { t } = useTranslation();
    const code = normalize(value);
    const tone = TONE_STYLE[STATUS_TONE[code]];
    const label = t(`pages.fakturownia.expenseForm.statuses.${code}`);

    const overlay = useMemo(
      () => (
        <Menu
          selectedKeys={[code]}
          onClick={({ key }) => {
            const next = String(key) as ExpenseStatusCode;
            if (next !== code) onChange?.(next);
          }}
        >
          {EXPENSE_STATUS_CODES.map((s) => (
            <Menu.Item key={s}>
              {t(`pages.fakturownia.expenseForm.statuses.${s}`)}
            </Menu.Item>
          ))}
        </Menu>
      ),
      [code, onChange, t],
    );

    if (readOnly || !onChange) {
      return (
        <Chip bg={tone.bg} fg={tone.fg} as="span">
          {label}
        </Chip>
      );
    }

    return (
      <Dropdown
        overlay={overlay}
        trigger={['click']}
        placement="bottomLeft"
        disabled={loading}
      >
        <Chip
          bg={tone.bg}
          fg={tone.fg}
          interactive
          type="button"
          aria-label={t('pages.fakturownia.expenseForm.fields.status')}
          title={t('pages.fakturownia.expenseForm.statusControl.hint')}
          onClick={(e) => e.stopPropagation()}
        >
          {label}
          <Caret aria-hidden>▾</Caret>
        </Chip>
      </Dropdown>
    );
  },
);

const Chip = styled.button<{ bg: string; fg: string; interactive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border: 0;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
  cursor: ${({ interactive }) => (interactive ? 'pointer' : 'default')};
`;

const Caret = styled.span`
  font-size: 9px;
  opacity: 0.85;
`;
